import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import JsonEditorForm from "../../components/JsonEditorForm";
import { localizationClient } from "../../api/v2/localizationClient";
import { RepositoryGroup, WorkspaceConfig } from "../../api/v2/types";
import { parseApiError } from "../../api/http/errors";
import { useToast } from "../../components/ui/toast/use-toast";
import { ToastAction } from "../../components/ui/toast/toast";
import EditorToolbar from "./components/EditorToolbar";
import { adaptEditorDataToLocalesList, buildSavePayload } from "./adapters/editorDataAdapter";
import { LocalesList } from "../../types/Locales";
import SectionState from "../shared/SectionState";
import HelpHoverCard from "../shared/HelpHoverCard";
import RepositoryEditorHeader from "./components/RepositoryEditorHeader";
import { EditorQuickFiltersState } from "./utils/editorPresentationUtils";

interface EditorTabProps {
  owner: string;
  repo: string;
  workspaceKey: string;
  branch: string;
  group: string;
  onWorkspaceUpdated: (workspace: WorkspaceConfig) => void;
  onBranchChange: (branch: string) => void;
  onGroupChange: (group: string) => void;
}

const EditorTab = ({
  owner,
  repo,
  workspaceKey,
  branch,
  group,
  onWorkspaceUpdated,
  onBranchChange,
  onGroupChange,
}: EditorTabProps) => {
  const { toast } = useToast();
  const [locales, setLocales] = useState<LocalesList[]>([]);
  const [branches, setBranches] = useState<Array<{ name: string; isWorkingBranch: boolean }>>([]);
  const [defaultBranch, setDefaultBranch] = useState("");
  const [workingBranch, setWorkingBranch] = useState<string | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [hasCommits, setHasCommits] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [activeBranch, setActiveBranch] = useState("");
  const [lastTreeUrl, setLastTreeUrl] = useState("");
  const [mergePreviewTotal, setMergePreviewTotal] = useState<number | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isPreviewingMerge, setIsPreviewingMerge] = useState(false);
  const lastSyncedBranchRef = useRef("");
  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilters, setQuickFilters] = useState<EditorQuickFiltersState>({
    showOnlyChanged: false,
    showEmpty: false,
    showMissing: false,
    compact: false,
  });

  const loadEditorData = useCallback(async () => {
    setIsLoading(true);
    setErrorText("");
    try {
      const [editorData, branchesData, repositoryGroups] = await Promise.all([
        localizationClient.getEditorData(owner, repo, {
          workspaceKey,
          group: group || undefined,
          branch: activeBranch || branch || undefined,
          autoMerge: true,
        }),
        localizationClient.getWorkspaceBranches(owner, repo, workspaceKey),
        localizationClient.getRepositoryGroups(owner, repo),
      ]);
      setLocales(adaptEditorDataToLocalesList(editorData));
      setBranches(branchesData.branches);
      setDefaultBranch(branchesData.defaultBranch || "");
      setWorkingBranch(branchesData.workingBranch || null);
      const groupKeys = (repositoryGroups.groups || []).map(
        (item: RepositoryGroup) => item.groupKey
      );
      setGroups(groupKeys);
      setActiveBranch(editorData.branch);
      if (
        editorData.branch &&
        editorData.branch !== branch &&
        lastSyncedBranchRef.current !== editorData.branch
      ) {
        lastSyncedBranchRef.current = editorData.branch;
        onBranchChange(editorData.branch);
      }
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
    } finally {
      setIsLoading(false);
    }
  }, [owner, repo, workspaceKey, group, branch, activeBranch, onBranchChange]);

  useEffect(() => {
    loadEditorData();
  }, [loadEditorData]);

  useEffect(() => {
    if (branch && branch === lastSyncedBranchRef.current) {
      lastSyncedBranchRef.current = "";
    }
  }, [branch]);

  const availableGroupsFromData = useMemo(() => {
    const set = new Set<string>();
    locales.forEach((locale) => {
      const metadataGroup = (locale.metadata as { group?: string }).group;
      if (metadataGroup) set.add(metadataGroup);
    });
    return Array.from(set);
  }, [locales]);

  const availableGroups = useMemo(() => {
    const merged = new Set<string>([...groups, ...availableGroupsFromData]);
    return Array.from(merged);
  }, [groups, availableGroupsFromData]);

  const handleSave = async (updatedJson: LocalesList[], commitMessage: string) => {
    if (!commitMessage.trim()) {
      toast({
        variant: "destructive",
        title: "Invalid commit message",
        description: "Commit message cannot be empty.",
      });
      return;
    }
    try {
      setIsSaving(true);
      setIsSyncing(true);
      await localizationClient.syncKeys(owner, repo, {
        workspaceKey,
        branch: activeBranch || branch || undefined,
        group: group || undefined,
        commitMessage: `chore(localization): sync keys before save`,
      });
      setIsSyncing(false);
      const response = await localizationClient.saveEditorData(owner, repo, {
        workspaceKey,
        commitMessage,
        locales: buildSavePayload(updatedJson),
      });
      setHasCommits(true);
      setLastTreeUrl(response.treeUrl);
      onWorkspaceUpdated(response.workspace);
      toast({
        description: "Changes were saved into workspace branch.",
        action: (
          <ToastAction asChild altText="Open branch tree">
            <Link to={response.treeUrl} target="_blank">
              Open
            </Link>
          </ToastAction>
        ),
      });
      await loadEditorData();
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
      toast({
        variant: "destructive",
        title: parsed.code,
        description: parsed.message,
      });
    }
    finally {
      setIsSyncing(false);
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!hasCommits) return;
    try {
      setIsPublishing(true);
      const response = await localizationClient.publishWorkspace(owner, repo, { workspaceKey });
      toast({
        description: "Pull request has been created.",
        action: (
          <ToastAction asChild altText="Open pull request">
            <Link to={response.pullRequestUrl} target="_blank">
              Open PR
            </Link>
          </ToastAction>
        ),
      });
      setHasCommits(false);
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
      toast({ variant: "destructive", title: parsed.code, description: parsed.message });
    }
    finally {
      setIsPublishing(false);
    }
  };

  const handleSwitchBranch = async (nextBranch: string) => {
    if (!nextBranch || nextBranch === activeBranch || isLoading || isSaving || isPublishing) return;
    try {
      await localizationClient.switchWorkspaceBranch(owner, repo, {
        workspaceKey,
        branch: nextBranch,
      });
      setActiveBranch(nextBranch);
      onBranchChange(nextBranch);
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
    }
  };

  const handleCreateBranch = async (nextBranch: string) => {
    const normalized = nextBranch.trim();
    if (!normalized) return;
    try {
      await localizationClient.createWorkspaceBranch(owner, repo, {
        workspaceKey,
        branch: normalized,
        fromBranch: defaultBranch || undefined,
      });
      setActiveBranch(normalized);
      onBranchChange(normalized);
      toast({
        description: `Branch "${normalized}" is now active as working branch.`,
      });
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
      toast({ variant: "destructive", title: parsed.code, description: parsed.message });
    }
  };

  const handlePreviewMerge = async () => {
    try {
      setIsPreviewingMerge(true);
      const preview = await localizationClient.previewMergeImpact(owner, repo, {
        workspaceKey,
        branch: activeBranch || branch || undefined,
        group: group || undefined,
      });
      setMergePreviewTotal(preview.totalAddedKeys);
      toast({
        description:
          preview.totalAddedKeys > 0
            ? `Merge preview: ${preview.totalAddedKeys} keys will be added by union merge.`
            : "Merge preview: no missing keys to add.",
      });
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
    } finally {
      setIsPreviewingMerge(false);
    }
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setQuickFilters({
      showOnlyChanged: false,
      showEmpty: false,
      showMissing: false,
      compact: false,
    });
  };

  return (
    <div className="space-y-4">
      <RepositoryEditorHeader
        owner={owner}
        repo={repo}
        workspaceKey={workspaceKey}
        branch={activeBranch || branch}
      />
      <HelpHoverCard
        title="Editor workflow"
        description="Use filters and search to focus rows, save to create commits, then publish pull request when review is ready."
      />
      <EditorToolbar
        group={group}
        groups={availableGroups}
        onGroupChange={onGroupChange}
        branch={activeBranch || branch}
        branches={branches}
        defaultBranch={defaultBranch}
        workingBranch={workingBranch}
        onBranchChange={handleSwitchBranch}
        onCreateBranch={handleCreateBranch}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        onResetFilters={handleResetFilters}
        onPreviewMerge={handlePreviewMerge}
        isPreviewingMerge={isPreviewingMerge}
        isDisabled={isLoading || isSaving || isPublishing || isSyncing}
      />
      {isSyncing ? <p className="text-xs text-muted-foreground">Synchronizing missing keys before save...</p> : null}
      {mergePreviewTotal !== null ? (
        <p className="text-xs text-muted-foreground">Last merge preview: {mergePreviewTotal} keys to add.</p>
      ) : null}
      <SectionState
        isLoading={isLoading}
        errorText={errorText}
        isEmpty={locales.length === 0}
        emptyText="No locale files in editor data. Configure bindings and reload editor."
        minHeightClassName="min-h-[320px]"
      >
        <JsonEditorForm
          localesList={locales}
          onSave={handleSave}
          onUpdate={handlePublish}
          hasCommits={hasCommits}
          isBusy={isSaving || isPublishing || isLoading}
          searchQuery={searchQuery}
          quickFilters={quickFilters}
          activeBranch={activeBranch || branch}
          defaultBranch={defaultBranch}
        />
      </SectionState>
      {lastTreeUrl ? (
        <p className="text-xs text-muted-foreground">
          Last saved tree:{" "}
          <Link to={lastTreeUrl} target="_blank" className="underline">
            {lastTreeUrl}
          </Link>
        </p>
      ) : null}
    </div>
  );
};

export default EditorTab;
